import * as React from 'react';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Flex } from '@/hen-ui/composites';
import { cn } from '@/hen-ui/utils';
import {
  loadingDotVariants,
  type LoadingDotVariantProps,
} from './loading-dots.variants';

export type LoadingDotsSize = NonNullable<LoadingDotVariantProps['size']>;
export type LoadingDotsColor = NonNullable<LoadingDotVariantProps['color']>;

/**
 * Flex `gap` token used between dots for each `size`. Kept local so the
 * public `size` API stays `sm | md | lg` (matching every other sized
 * hen-ui component) instead of leaking the spacing-token scale.
 */
const GAP_BY_SIZE: Record<LoadingDotsSize, 'xxs' | 'md' | 'lg'> = {
  sm: 'xxs',
  md: 'md',
  lg: 'lg',
};

export interface LoadingDotsProps {
  /** Accessible loading message for screen readers. */
  label?: string;
  /**
   * Visual size of each dot. Defaults to `md` (32px) to preserve the
   * full-surface loading behavior; use `sm` (8px) for inline indicators
   * like chat-typing affordances, or `lg` (48px) for hero loading.
   */
  size?: LoadingDotsSize;
  /** Dot color mapped to HEN design tokens. Defaults to `"white"`. */
  color?: LoadingDotsColor;
}

const Dot: React.FC<{
  animationClass: string;
  size: LoadingDotsSize;
  color: LoadingDotsColor;
}> = ({ animationClass, size, color }) => (
  <div
    className={cn(loadingDotVariants({ size, color }), animationClass)}
    aria-hidden
  />
);

export const LoadingDots: React.FC<LoadingDotsProps> = ({
  label = 'Loading...',
  size = 'md',
  color = 'white',
}) => {
  return (
    <Flex
      direction='row'
      justify='center'
      align='center'
      gap={GAP_BY_SIZE[size]}
      role='status'
      aria-live='polite'
      data-hen-id={COMPONENT_IDS.LoadingDots}
      data-size={size}
    >
      <Dot
        key='animate-loadingDot1'
        animationClass='animate-loadingDot1'
        size={size}
        color={color}
      />
      <Dot
        key='animate-loadingDot2'
        animationClass='animate-loadingDot2'
        size={size}
        color={color}
      />
      <Dot
        key='animate-loadingDot3'
        animationClass='animate-loadingDot3'
        size={size}
        color={color}
      />
      <span className='sr-only'>{label}</span>
    </Flex>
  );
};
